import React, { useEffect, useState, useMemo } from 'react';
import { RuleCompareOperator } from '@bringg/types';
import { OptionWrapper } from 'components/select/select';
import { Condition } from '../rule/rule-utils';
import SingleNumericRow from './single-numeric-condition';
import MultiCondition, { MultiConditionTranslations } from '../multi-condition/multi-condition';
import { Numeric } from './types';

export interface NumericTranslations extends MultiConditionTranslations {
	equals: string;
	notEquals: string;
	lessThan: string;
	lessThanOrEquals: string;
	greaterThan: string;
	greaterThanOrEquals: string;
	inRange: string;
	validations?: {
		onlyPositive: string;
		toGreaterThanFrom: string;
	};
}

export interface NumericConditionProps {
	disabled?: boolean;
	index?: number;
	maxAttributePathItems?: number;
	condition: Condition;
	translations: NumericTranslations;
	withIndexedLabel?: boolean;
	parentContainerClass?: string;

	onChange(condition: Condition, value: Numeric[]): void;

	onRemove(condition: Condition): void;
}

const NumericCondition = ({
	index,
	condition,
	onRemove,
	translations,
	parentContainerClass,
	onChange,
	maxAttributePathItems,
	withIndexedLabel = true,
	disabled = false
}: NumericConditionProps) => {
	const [options, setOptions] = useState<OptionWrapper[]>([]);
	const multiConditionTranslations = useMemo(
		() => ({
			addRow: translations.addRow,
			removeTooltipText: translations.removeTooltipText
		}),
		[translations]
	);

	useEffect(() => {
		const allOptions = [
			{ id: RuleCompareOperator.EQUALS, name: translations.equals },
			{ id: RuleCompareOperator.NOT_EQUALS, name: translations.notEquals },
			{ id: RuleCompareOperator.LESS_THAN, name: translations.lessThan },
			{ id: RuleCompareOperator.LESS_THAN_OR_EQUALS, name: translations.lessThanOrEquals },
			{ id: RuleCompareOperator.GREATER_THAN, name: translations.greaterThan },
			{ id: RuleCompareOperator.GREATER_THAN_OR_EQUALS, name: translations.greaterThanOrEquals },
			{ id: RuleCompareOperator.IN_RANGE, name: translations.inRange }
		];

		const filteredOptions = condition.allowedOperators
			? allOptions.filter(({ id }) => condition.allowedOperators.includes(id))
			: allOptions;

		setOptions(filteredOptions);
	}, [translations, condition.allowedOperators]);

	return (
		<MultiCondition
			disabled={disabled}
			index={index}
			maxAttributePathItems={maxAttributePathItems}
			translations={multiConditionTranslations}
			condition={condition}
			onRemove={onRemove}
			onChange={onChange}
			withIndexedLabel={withIndexedLabel}
			rowClassName="numeric-row"
		>
			{({ singleCondition, setIsValid, onChange }) => (
				<SingleNumericRow
					disabled={disabled}
					onChange={onChange}
					setIsValid={setIsValid}
					singleCondition={singleCondition}
					parentContainerClass={parentContainerClass}
					options={options}
					validations={{
						onlyPositive: translations.validations?.onlyPositive,
						toGreaterThanFrom: translations.validations?.toGreaterThanFrom
					}}
				/>
			)}
		</MultiCondition>
	);
};

export default NumericCondition;
